import { baseApi as api } from './api.base.js';
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    apiControllerDeleteTask: build.mutation<
      ApiControllerDeleteTaskApiResponse,
      ApiControllerDeleteTaskApiArg
    >({
      query: (queryArg) => ({
        url: `/web/delete_task/${queryArg.taskId}`,
        method: 'DELETE',
      }),
    }),
    apiControllerGetTasks: build.query<
      ApiControllerGetTasksApiResponse,
      ApiControllerGetTasksApiArg
    >({
      query: (queryArg) => ({
        url: `/web/tasks/${queryArg.vaultId}/${queryArg.all}/${queryArg.limit}/${queryArg.page}`,
        params: {
          pair: queryArg.pair,
          type: queryArg['type'],
          direction: queryArg.direction,
          taskId: queryArg.taskId,
          sort: queryArg.sort,
        },
      }),
    }),
    apiControllerGetTask: build.query<ApiControllerGetTaskApiResponse, ApiControllerGetTaskApiArg>({
      query: (queryArg) => ({ url: `/web/task/${queryArg.taskId}` }),
    }),
    apiControllerAddTask: build.mutation<
      ApiControllerAddTaskApiResponse,
      ApiControllerAddTaskApiArg
    >({
      query: (queryArg) => ({
        url: `/web/add_task/${queryArg.vaultId}`,
        method: 'POST',
        body: queryArg.taskUpsertRequest,
      }),
    }),
    apiControllerUpdateTask: build.mutation<
      ApiControllerUpdateTaskApiResponse,
      ApiControllerUpdateTaskApiArg
    >({
      query: (queryArg) => ({
        url: `/web/update_task/${queryArg.taskId}`,
        method: 'PUT',
        body: queryArg.taskUpsertRequest,
      }),
    }),
    apiControllerUpdateTaskStatus: build.mutation<
      ApiControllerUpdateTaskStatusApiResponse,
      ApiControllerUpdateTaskStatusApiArg
    >({
      query: (queryArg) => ({
        url: `/web/update_status_task/${queryArg.taskId}/${queryArg.action}`,
        method: 'PUT',
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as generatedApi };
export type ApiControllerDeleteTaskApiResponse =
  /** status 200 Task deleted (soft) and orders removed */ DeleteTaskResponse;
export type ApiControllerDeleteTaskApiArg = {
  /** Task ID */
  taskId: number;
};
export type ApiControllerGetTasksApiResponse =
  /** status 200 Returns paginated tasks */ TasksResponse;
export type ApiControllerGetTasksApiArg = {
  /** Vault ID for filtering (0 = all vaults) */
  vaultId: number;
  /** Show all tasks (1) or filtered by vault (0) */
  all: number;
  /** Number of items per page */
  limit: number;
  /** Page number */
  page: number;
  /** Search by trading pair (e.g. "BTC/USDT") */
  pair?: string;
  /** Filter by task type (single number or comma-separated list, e.g. "0,1,2") */
  type?: string;
  /** Filter by direction (0 or 1) */
  direction?: number;
  /** Filter by specific task ID */
  taskId?: number;
  /** Sort order: "date:desc" (newest first, default), "date:asc" (oldest first), "status:active" (running tasks first) */
  sort?: string;
};
export type ApiControllerGetTaskApiResponse = /** status 200 Returns task details */ TaskResponse;
export type ApiControllerGetTaskApiArg = {
  /** Task ID */
  taskId: number;
};
export type ApiControllerAddTaskApiResponse =
  /** status 200 Task added successfully */ TaskUpsertResponse;
export type ApiControllerAddTaskApiArg = {
  /** Vault ID */
  vaultId: number;
  /** Task configuration. Unknown fields are ignored. Defaults are applied per algorithm type. */
  taskUpsertRequest: TaskUpsertRequest;
};
export type ApiControllerUpdateTaskApiResponse =
  /** status 200 Task updated successfully */ TaskUpsertResponse;
export type ApiControllerUpdateTaskApiArg = {
  /** Task ID */
  taskId: number;
  /** Task configuration (same shape as add_task). Unknown fields are ignored. */
  taskUpsertRequest: TaskUpsertRequest;
};
export type ApiControllerUpdateTaskStatusApiResponse =
  /** status 200 Task status updated */ UpdateStatusTaskResponse;
export type ApiControllerUpdateTaskStatusApiArg = {
  /** Task ID */
  taskId: number;
  /** Action: start or stop */
  action: string;
};
export type DeleteTaskData = {
  /** Task ID that was soft-deleted */
  taskId: number;
};
export type DeleteTaskResponse = {
  /** Operation status */
  status: boolean;
  data: DeleteTaskData;
};
export type TaskListItem = {
  /** Task ID */
  id: number;
  /** Vault ID */
  pId: number;
  /** Algorithm type (see TaskUpsertRequest.type). 5/6 are CL Moving/Fixed Range. */
  type: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** Direction (0 sell, 1 buy, 2 both) */
  direction: 0 | 1 | 2;
  /** Active flag (0 inactive, 1 active, 2 deleted) */
  active: 0 | 1 | 2;
  basePriceInCurrency?: string;
  basePriceMin?: number;
  basePriceMax?: number;
  txAmountCurrency?: string;
  txAmountMin?: number;
  txAmountMax?: number;
  txImpactMax?: number;
  /** Lower bound (minutes) of the random delay between trades */
  tradeFillFrom?: number;
  /** Upper bound (minutes) of the random delay between trades */
  tradeFillTo?: number;
  priceOut?: number;
  /** Currency the `limit` field is denominated in: token symbol (base/quote) or "USD" */
  limitCurrency?: string;
  /** Total trade volume cap for the task, denominated in `limitCurrency`. Reaching it auto-disables the task. */
  limit?: number;
  /** Cumulative input-side traded volume, in USD */
  amountIn?: number;
  /** Cumulative output-side traded volume, in USD */
  amountOut?: number;
  /** Cumulative traded volume in USD (mirrors `amountIn`; provided as an explicit, unit-stable field for clients) */
  volumeUSD?: number;
  /** `limit` converted to USD using `limitCurrency` and the latest vault price snapshot (`vault.priceInQuote` / `vault.priceInUsd`). 0 when conversion is not possible (e.g. no price snapshot yet or unknown currency). */
  limitUSD?: number;
  /** Cumulative native gas spent */
  gasSpent?: number;
  txCount?: number;
  slippage?: number;
  /** Author display name */
  authorName?: string | null;
  /** ISO creation timestamp */
  created?: string | null;
  /** Vault label (only when fetching across all vaults) */
  vaultLabel?: string | null;
  /** Trading pair string base/quote */
  tradingPair?: string | null;
  nativeTokenName?: string | null;
  blockchainName?: string | null;
  exchangeName?: string | null;
  exchangeProtocol?: string | null;
  exchangePoolDeployer?: string | null;
};
export type TasksData = {
  /** Total tasks matching the filter */
  total?: number;
  /** Tasks list */
  data: TaskListItem[];
};
export type TasksResponse = {
  /** Operation status */
  status: boolean;
  data: TasksData;
};
export type TaskData = {
  direction: 0 | 1 | 2;
  /** Algorithm type (see TaskUpsertRequest.type). 5/6 are CL Moving/Fixed Range. */
  type: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  basePriceInCurrency: string;
  basePriceMin: number;
  basePriceMax: number;
  txAmountCurrency: string;
  txAmountMin: number;
  txAmountMax: number;
  txImpactMax: number;
  tradeFillFrom: number;
  tradeFillTo: number;
  limitCurrency: string;
  limit: number;
  slippage: number;
};
export type TaskResponse = {
  /** Operation status */
  status: boolean;
  data: TaskData;
};
export type TaskUpsertData = {
  /** Task ID */
  taskId?: number;
  /** Result message */
  message?: string;
};
export type TaskUpsertResponse = {
  /** Operation status */
  status: boolean;
  data: TaskUpsertData;
};
export type TaskUpsertRequest = {
  /** Task algorithm type:
    - `0` — Mirror (long-running, mirrors external trades)
    - `1` — Iceberg (long-running, randomized periodic trades)
    - `2` — External (one-shot, swap from API). `direction` forced to 2.
    - `3` — External limit order. `direction` forced to 2; requires `txImpactMax >= 1`.
    - `4` — Ping-Pong / future long-running algos.
    - `5` — CL Moving Range (requires CL vault, vault.type=3). Maintains a concentrated-liquidity position and rebalances when price drifts out of the safety band.
    - `6` — CL Fixed Range (requires CL vault). Opens a fixed-range position without rebalancing. */
  type: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** CL tasks only (type 5/6): how `basePriceMin`/`basePriceMax` are interpreted before being converted to Uniswap V3 ticks server-side:
    - `ticks` — raw integer ticks
    - `price` — quote-per-base human price for each bound
    - `percent` — signed % offsets around the current pool price (e.g. -5 / +5)
    For CL tasks `txImpactMax` is the rebalance safety band in percent (0..50) and `slippage` is in percent; both are stored as fractions. `txAmountMin` is the minimum seconds between rebalances. */
  rangeMode?: 'ticks' | 'price' | 'percent';
  /** Trade direction:
    - `0` — sell base
    - `1` — buy base
    - `2` — random / both (auto for type 2/3) */
  direction: 0 | 1 | 2;
  /** Auto-start the task immediately after create (1) or keep it inactive (0). */
  start?: 0 | 1;
  /** Currency in which the price range below is interpreted: `USD` or quote-token symbol (e.g. `USDC`). */
  basePriceInCurrency?: string;
  /** Lower price bound; trade is skipped when current price < basePriceMin (0 = no lower bound) */
  basePriceMin?: number;
  /** Upper price bound; trade is skipped when current price > basePriceMax (0 = no upper bound) */
  basePriceMax?: number;
  /** Currency for the per-trade amount range: `USD`, base- or quote-token symbol. */
  txAmountCurrency?: string;
  /** Minimum per-trade amount (in `txAmountCurrency`). */
  txAmountMin?: number;
  /** Maximum per-trade amount (in `txAmountCurrency`). */
  txAmountMax?: number;
  /** Maximum allowed price impact (fraction, e.g. `0.01` = 1%). For type=3 (limit) clamped to >= 1. */
  txImpactMax?: number;
  /** Lower bound (minutes) of the random delay between iceberg trades. */
  tradeFillFrom?: number;
  /** Upper bound (minutes) of the random delay between iceberg trades. */
  tradeFillTo?: number;
  /** Currency for the cumulative volume cap below: `USD`, base- or quote-token symbol. */
  limitCurrency?: string;
  /** Cumulative volume cap; once reached the worker stops trading. 0 = no cap. */
  limit?: number;
  /** Slippage tolerance in percent. Allowed range 0.5..100. Out-of-range values fall back to 0.5. */
  slippage?: number;
  /** Optional explicit price-out for limit orders / external swaps (0 = unset). */
  priceOut?: number;
  /** Send Telegram notifications about trades to the vault Telegram chat (0/1). */
  notify?: number;
  /** Send Telegram notifications about trades to the global admin chat (0/1). */
  adminNotify?: number;
  /** Comma-separated additional Telegram chat IDs to receive notifications. May contain negatives. Empty = none. */
  otherNotify?: string;
};
export type UpdateStatusTaskData = {
  /** Task ID */
  taskId: number;
  /** Requested action */
  action: 'start' | 'stop';
  /** Resulting active flag (0 or 1) */
  active: number;
  /** Result message */
  message: string;
};
export type UpdateStatusTaskResponse = {
  /** Operation status */
  status: boolean;
  data: UpdateStatusTaskData;
};
